/**
 * Copyright 2023-2024, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { v4 as uuidV4 } from 'uuid';
import { odpIntegrationsAreEqual } from './odp_config';
import { OdpEvent } from './event_manager/odp_event';
import { resolvablePromise } from '../utils/promise/resolvablePromise';
import { BaseService, ServiceState } from '../service';
import { CLIENT_VERSION, JAVASCRIPT_CLIENT_ENGINE } from '../utils/enums';
import { ODP_DEFAULT_EVENT_TYPE, ODP_EVENT_ACTION, ODP_USER_KEY } from './constant';
import { isVuid } from '../vuid/vuid';
import { sprintf } from '../utils/fns';
import { SERVICE_STOPPED_BEFORE_RUNNING } from '../service';
import { ODP_IDENTIFY_NOT_DISPATCHED } from '../message/log_message';
export const LOGGER_NAME = 'OdpManager';
export class DefaultOdpManager extends BaseService {
    constructor(config) {
        super();
        this.clientEngine = JAVASCRIPT_CLIENT_ENGINE;
        this.clientVersion = CLIENT_VERSION;
        this.segmentManager = config.segmentManager;
        this.eventManager = config.eventManager;
        this.configPromise = resolvablePromise();
        if (config.userAgentParser) {
            const { os, device } = config.userAgentParser.parseUserAgentInfo();
            const userAgentInfo = {
                'os': os.name,
                'os_version': os.version,
                'device_type': device.type,
                'model': device.model,
            };
            this.userAgentData = new Map(Object.entries(userAgentInfo).filter(([_, value]) => value != null && value != undefined));
        }
        if (config.logger) {
            this.setLogger(config.logger);
        }
    }
    setLogger(logger) {
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
        this.eventManager.setLogger(logger.child());
        this.segmentManager.setLogger(logger.child());
    }
    setClientInfo(clientEngine, clientVersion) {
        this.clientEngine = clientEngine;
        this.clientVersion = clientVersion;
    }
    start() {
        if (!this.isNew()) {
            return;
        }
        this.state = ServiceState.Starting;
        this.eventManager.start();
        const startDependencies = [
            this.configPromise,
            this.eventManager.onRunning(),
        ];
        Promise.all(startDependencies)
            .then(() => {
            this.handleStartSuccess();
        }).catch((err) => {
            this.handleStartFailure(err);
        });
    }
    makeDisposable() {
        super.makeDisposable();
        this.eventManager.makeDisposable();
    }
    handleStartSuccess() {
        if (this.isDone()) {
            return;
        }
        this.state = ServiceState.Running;
        this.startPromise.resolve();
    }
    handleStartFailure(error) {
        if (this.isDone()) {
            return;
        }
        this.state = ServiceState.Failed;
        this.startPromise.reject(error);
        this.stopPromise.reject(error);
    }
    flushImmediately() {
        if (!this.isRunning()) {
            return Promise.resolve();
        }
        return this.eventManager.flushImmediately();
    }
    stop() {
        if (this.isDone()) {
            return;
        }
        if (!this.isRunning()) {
            this.startPromise.reject(new Error(sprintf(SERVICE_STOPPED_BEFORE_RUNNING, 'OdpManager')));
        }
        this.state = ServiceState.Stopping;
        this.eventManager.stop();
        this.eventManager.onTerminated().then(() => {
            this.state = ServiceState.Terminated;
            this.stopPromise.resolve();
        }).catch((err) => {
            this.state = ServiceState.Failed;
            this.stopPromise.reject(err);
        });
    }
    updateConfig(odpIntegrationConfig) {
        // do nothing if config did not change
        if (this.odpIntegrationConfig && odpIntegrationsAreEqual(this.odpIntegrationConfig, odpIntegrationConfig)) {
            return false;
        }
        if (this.isDone()) {
            return false;
        }
        this.odpIntegrationConfig = odpIntegrationConfig;
        this.configPromise.resolve();
        this.segmentManager.updateConfig(odpIntegrationConfig);
        this.eventManager.updateConfig(odpIntegrationConfig);
        return true;
    }
    /**
     * Attempts to fetch and return a list of a user's qualified segments from the local segments cache.
     * If no cached data exists for the target user, this fetches and caches data from the ODP server instead.
     * @param {string}                          userId  - Unique identifier of a target user.
     * @param {Array<OptimizelySegmentOption>}  options - An array of OptimizelySegmentOption used to ignore and/or reset the cache.
     * @returns {Promise<string[] | null>}      A promise holding either a list of qualified segments or null.
     */
    fetchQualifiedSegments(userId, options = []) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isVuid(userId)) {
                return this.segmentManager.fetchQualifiedSegments(ODP_USER_KEY.VUID, userId, options);
            }
            return this.segmentManager.fetchQualifiedSegments(ODP_USER_KEY.FS_USER_ID, userId, options);
        });
    }
    identifyUser(userId, vuid) {
        var _a;
        const identifiers = new Map();
        let finalUserId = userId;
        let finalVuid = vuid;
        if (!vuid && isVuid(userId)) {
            finalVuid = userId;
            finalUserId = undefined;
        }
        if (finalVuid) {
            identifiers.set(ODP_USER_KEY.VUID, finalVuid);
        }
        if (finalUserId) {
            identifiers.set(ODP_USER_KEY.FS_USER_ID, finalUserId);
        }
        // Identify requires 2+ identifiers to link (e.g., vuid + fs_user_id).
        // A single identifier has no cross-reference value and generates unnecessary traffic.
        if (identifiers.size < 2) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.debug(ODP_IDENTIFY_NOT_DISPATCHED);
            return;
        }
        const event = new OdpEvent(ODP_DEFAULT_EVENT_TYPE, ODP_EVENT_ACTION.IDENTIFIED, identifiers);
        this.sendEvent(event);
    }
    sendEvent(event) {
        if (!event.identifiers.has(ODP_USER_KEY.VUID) && this.vuid) {
            event.identifiers.set(ODP_USER_KEY.VUID, this.vuid);
        }
        event.data = this.augmentCommonData(event.data);
        this.eventManager.sendEvent(event);
    }
    augmentCommonData(sourceData) {
        const data = new Map(this.userAgentData);
        data.set('idempotence_id', uuidV4());
        data.set('data_source_type', 'sdk');
        data.set('data_source', this.clientEngine);
        data.set('data_source_version', this.clientVersion);
        sourceData.forEach((value, key) => data.set(key, value));
        return data;
    }
    setVuid(vuid) {
        this.vuid = vuid;
        this.onRunning().then(() => {
            var _a;
            if ((_a = this.odpIntegrationConfig) === null || _a === void 0 ? void 0 : _a.integrated) {
                const event = new OdpEvent(ODP_DEFAULT_EVENT_TYPE, ODP_EVENT_ACTION.INITIALIZED);
                this.sendEvent(event);
            }
        });
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=odp_manager.js.map